import { cloneElement, useState, useCallback, useEffect, useRef } from 'react'
import { css } from '@emotion/react'
import { Avatar } from '@mui/material'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'

const menuItemTextStyles = css`
  transition: opacity 300ms ease-in-out;

  .page-with-nav__drawer[aria-expanded='false'] & {
    opacity: 0;
  }
`

const mainNavStyles = css`
  .main-navigation__menu-title {
    transition: opacity 300ms ease-in-out;

    .page-with-nav__drawer[aria-expanded='false'] & {
      opacity: 0;
    }
  }

  .main-navigation__header {
    transition: padding-left 300ms ease-in-out;

    .page-with-nav__drawer[aria-expanded='false'] & {
      padding-left: 11px;
    }
  }
`

const stylesWithinCollapsedNestedMenuItem = css`
  .main-navigation__nested-menu-wrapper {
    overflow: hidden;
    width: 44px;
    border-radius: 4px;
    background-color: #2b5562 !important;

    svg {
      color: var(--clvn-green-500);
    }
  }
`

const nestedMenuStyles = css`
  .-nested-menu-expanded {
    .page-with-nav__drawer[aria-expanded='false'] & {
      grid-template-rows: 0fr;
    }
  }

  .main-navigation__nested-menu-wrapper {
    .page-with-nav__drawer[aria-expanded='false'] & {
      background-color: transparent;
    }
  }

  .main-navigation__nested-menu-text {
    transition: all 300ms ease-in-out;

    .page-with-nav__drawer[aria-expanded='false'] & {
      opacity: 0;
    }
  }

  .main-navigation__button-accord {
    transition: all 300ms ease-in-out;

    .page-with-nav__drawer[aria-expanded='false'] & {
      opacity: 0;
    }
  }
`

export const MainNav = ({ children }) => {
  return (
    <div css={mainNavStyles} className="main-navigation flex flex-col flex-1 bg-cv-blue-800 gap-5">
      {children}
    </div>
  )
}

const Menu = ({ children }) => {
  return <nav className="main-navigation__menu flex flex-col gap-1">{children}</nav>
}

const AccountLogo = ({ slug, name }) => {
  return slug ? (
    <Avatar alt={`${name}-avatar`} src={`/api/v1/logo/${slug}`} variant="rounded" />
  ) : (
    <Avatar variant="rounded">
      {name
        ? name
            .split(' ')
            .map(part => {
              return part[0]
            })
            .join('')
        : ''}
    </Avatar>
  )
}

const Header = ({ slug, name }) => {
  return (
    <header className="main-navigation__header flex gap-5 px-md pt-md items-center">
      <AccountLogo slug={slug} name={name} />
      <h1 className="text-sm text-cv-blue-300 font-bold">{name}</h1>
    </header>
  )
}

const MenuTitle = ({ children }) => {
  return (
    <h3 className="main-navigation__menu-title text-[12px] text-cv-blue-300 px-5">{children}</h3>
  )
}

interface IMenuItem {
  to: string
  text: string
  active: boolean
  icon?: React.ReactNode
}

const MenuItemLink = ({ to, children }) => {
  return (
    <Link
      className="main-navigation__menu-item-link text-nowrap flex-inline text-[13px] text-cv-blue-300 flex gap-5 items-center px-xs py-2 text-white"
      to={to}
    >
      {children}
    </Link>
  )
}

const MenuItem = ({ to, text, active = false, icon = null }: IMenuItem) => {
  const activeMenuItemClasses = active ? 'bg-[#2B5562] rounded-md' : 'bg-[#1B3F4A]'
  const activePsuedoClass = active
    ? 'before:border-l-4 before:border-cv-green-500 before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1'
    : ''

  return (
    <div className={`main-navigation__menu-item relative px-2.5 py-1 ${activePsuedoClass}`}>
      <div className={activeMenuItemClasses}>
        <MenuItemLink to={to}>
          {icon}
          <span css={menuItemTextStyles}>{text}</span>
        </MenuItemLink>
      </div>
    </div>
  )
}

interface PortalNestedMenuProps {
  children: React.ReactNode
  position: PortalPosition
  show: boolean
}

const PortalNestedMenu = ({ children, position }: PortalNestedMenuProps) => {
  const portalVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
    exit: {
      opacity: 0,
      x: 20,
      transition: {
        duration: 0.3,
        ease: 'easeIn',
      },
    },
  }

  return createPortal(
    <motion.div
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={portalVariants}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 1000,
      }}
    >
      {children}
    </motion.div>,
    document.body,
  )
}

interface PortalPosition {
  top: number
  left: number
}

const usePortal = () => {
  const [showPortal, setShowPortal] = useState(false)
  const [portalPosition, setPortalPosition] = useState<PortalPosition>({ top: 0, left: -100 })

  const showPortalWithPosition = useCallback((position: PortalPosition) => {
    setPortalPosition(position)
    setShowPortal(true)
  }, [])

  const hidePortal = useCallback(() => {
    setShowPortal(false)
  }, [])

  return { showPortal, portalPosition, showPortalWithPosition, hidePortal }
}

const NestedMenu = ({ to, text, icon, active = false, children, ariaExpandedValue }) => {
  const [isHovering, setIsHovering] = useState(false)
  const { showPortal, portalPosition, showPortalWithPosition, hidePortal } = usePortal()
  const menuRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(active)
  const ariaId = text.split(' ').join('-').toLowerCase()
  const expandedClasses = open
    ? '-nested-menu-expanded grid-rows-[1fr] opacity-100'
    : 'grid-rows-[0fr] opacity-0'

  const activeNestedMenuItemClasses = active ? 'bg-[#2B5562] rounded-md' : ''

  useEffect(() => {
    setOpen(active)
  }, [active])

  const handleMouseEnter = useCallback(() => {
    if (ariaExpandedValue) return

    setIsHovering(true)
    setOpen(true) // Default the menu to open when hovering during collapsed state.
    // start counting and setOpen to true if we're still hovering for 1 second
    const rect = menuRef.current?.getBoundingClientRect()
    if (rect) {
      const NAV_WIDTH = 65
      const NAV_PADDING = 15
      showPortalWithPosition({ top: rect.top, left: rect.left + NAV_WIDTH + NAV_PADDING })
    }
  }, [ariaExpandedValue, showPortalWithPosition])

  const portalMouseEnter = useCallback(() => {
    setOpen(true)
    setIsHovering(true)
  }, [])

  const portalMouseLeave = useCallback(() => {
    setIsHovering(false)
  }, [])

  const handleMouseLeave = useCallback(() => {
    if (ariaExpandedValue) return

    setIsHovering(false)
    setOpen(false) // Prevents stacking on hover; provides nice animation.
  }, [ariaExpandedValue])

  useEffect(() => {
    if (!isHovering) {
      const timer = setTimeout(() => {
        hidePortal()
      }, 250)
      return () => {
        clearTimeout(timer)
      }
    }
  }, [isHovering, hidePortal])

  const menuContent = (
    <div css={nestedMenuStyles} className="main-navigation__nested-menu px-xs py-1">
      <div
        className={`main-navigation__nested-menu-wrapper transition-all duration-[300ms] w-full ${activeNestedMenuItemClasses}`}
      >
        <div className="flex gap-3 justify-between pr-3">
          <MenuItemLink to={to}>
            {icon}
            <span className="main-navigation__nested-menu-text flex-inline flex-1">{text}</span>
          </MenuItemLink>
          <button
            type="button"
            className="main-navigation__button-accord p-0 text-cv-blue-300"
            aria-controls={ariaId}
            aria-expanded={open}
            aria-label="Toggle Nested Menu"
            onClick={() => {
              setOpen(!open)
            }}
          >
            <ChevronDown strokeWidth={1.5} />
          </button>
        </div>
        <div
          id={ariaId}
          className={`transition-all duration-300 ease-in-out grid ${expandedClasses}`}
        >
          <div className="overflow-hidden">{children}</div>
        </div>
      </div>
    </div>
  )

  return (
    <>
      <div
        ref={menuRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="main-navigation__nested-menu-controller"
        css={active && !ariaExpandedValue ? stylesWithinCollapsedNestedMenuItem : null}
      >
        {menuContent}
      </div>
      <AnimatePresence>
        {showPortal && (
          <PortalNestedMenu show={showPortal} position={portalPosition}>
            {cloneElement(menuContent, {
              style: {
                backgroundColor: '#2B5562',
                borderRadius: '6px',
              },
              onMouseEnter: portalMouseEnter,
              onMouseLeave: portalMouseLeave,
            })}
          </PortalNestedMenu>
        )}
      </AnimatePresence>
    </>
  )
}

const NestedMenuItem = ({ to, text, active = false }) => {
  const activeDotClass = active ? 'bg-cv-green-500' : 'bg-[#2B5562]'
  const activeTextClass = active ? 'text-cv-green-500' : 'text-white'

  return (
    <Link
      className={`flex main-navigation__nested-menu-item gap-5 px-xs py-[6px] last:pb-3 ${activeTextClass} hover:text-cv-green-500 items-center text-[11px] transition duration-200`}
      to={to}
    >
      <span className="inline-flex w-[24px] justify-center">
        <span
          className={`inline-flex rounded-md ${activeDotClass} h-3 w-3 transition duration-300 ease-in-out`}
        />
      </span>
      {text}
    </Link>
  )
}

MainNav.Menu = Menu
MainNav.Header = Header
MainNav.MenuItem = MenuItem
MainNav.NestedMenu = NestedMenu
MainNav.NestedMenuItem = NestedMenuItem
MainNav.MenuTitle = MenuTitle
MainNav.Menu = Menu
